'use client';

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import MainHeader from './MainHeader';
import MainPageContent from './MainPageContent';
import Sidebar from '../Sidebar/Sidebar';
import AboutMenu from './AboutMenu';
import { useI18nReady } from '@/hooks/useI18nReady';
import { useIsUserAuthorized } from '@/utils/authHelpers';
import { Separator } from '../ui/separator';
import samplesSelectorModel from '../Samples/SamplesSelector/SamplesSelectorModel';
import { reaction } from 'mobx';

type MainPageProps = {
  mode?: 'samples' | 'about';
};

const MainPage = ({ mode = 'samples' }: MainPageProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [selectedComposer, setSelectedComposer] = useState<string | null>(null);
  const [samplesLoaded, setSamplesLoaded] = useState(false);
  const { t } = useTranslation();
  const isI18nReady = useI18nReady();
  const isUserAuthorized = useIsUserAuthorized();

  // Track when samples are loaded via MobX reaction
  useEffect(() => {
    const disposer = reaction(
      () => samplesSelectorModel.samples.length,
      (samplesLength) => {
        if (samplesLength > 0) {
          setSamplesLoaded(true);
        }
      },
      { fireImmediately: true }
    );

    return () => disposer();
  }, []);

  useEffect(() => {
    if (!isUserAuthorized) {
      setShowFavoritesOnly(false);
    }
  }, [isUserAuthorized]);

  // Wait for i18n to initialize to prevent hydration mismatch
  if (!isI18nReady) {
    return (
      <div className='h-screen flex flex-col'>
        <MainHeader />
        <div className='flex flex-row flex-1 overflow-hidden' />
      </div>
    );
  }

  // Note: Russian has no titleEnd, so conditionally render it
  const titleEnd = t('mainPage.sidebar.titleEnd');

  return (
      <div className='h-screen flex flex-col'>
        <MainHeader />
        <div className='flex flex-row flex-1 overflow-hidden'>
          <Sidebar className='hidden xl:block p-6 sm:p-8 w-91 flex-shrink-0'>
            {mode === 'samples' ? (
              <>
                <Sidebar.Header
                  title={
                    <>
                      {t('mainPage.sidebar.title')}{' '}
                      <span className='text-accent-blue'>{t('mainPage.sidebar.titleHighlight')}</span>
                      {titleEnd && ` ${titleEnd}`}
                    </>
                  }
                  onSearchChange={setSearchQuery}
                />
                <Sidebar.Content>
                  {isUserAuthorized && (
                    <>
                    <Sidebar.FilterGroup>                  
                      <Sidebar.FilterItem
                        label={t('mainPage.sidebar.filters.favorites')}
                        checked={showFavoritesOnly}
                        onChange={setShowFavoritesOnly}
                      />          
                    </Sidebar.FilterGroup>
                     {/* <Separator className='my-2 bg-zinc-200' /> */}
                     </>
                  )}
                 
                  {/* <Sidebar.FilterGroup title={t('mainPage.sidebar.filters.difficultyLevel')}>
                    <Sidebar.FilterItem label={t('mainPage.sidebar.filters.beginner')} />
                    <Sidebar.FilterItem label={t('mainPage.sidebar.filters.intermediate')} />
                    <Sidebar.FilterItem label={t('mainPage.sidebar.filters.advanced')} />
                  </Sidebar.FilterGroup> */}
                  <Separator className='my-2 bg-zinc-200' />
                  {samplesLoaded && (
                    <Sidebar.ComposerFilterGroup
                      samples={samplesSelectorModel.samples}
                      selectedComposer={selectedComposer}
                      onComposerChange={setSelectedComposer}
                    />
                  )}
                </Sidebar.Content>
              </>
            ) : (
              <AboutMenu />
            )}
          </Sidebar>

          <div className='flex-1 overflow-y-auto'>
            <MainPageContent
              mode={mode}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              showFavoritesOnly={showFavoritesOnly}
              selectedComposer={selectedComposer}
            />
          </div>
        </div>
      </div>
  );
};

export default MainPage;
